"use client";

import React from "react";

export const GOOGLE_CONSENT_OPEN_EVENT = "openings:google-consent-open";
export const GOOGLE_CONSENT_TRIGGER_SELECTOR =
  "[data-openings-privacy-choices-trigger]";

type GoogleConsentOpenDetail = {
  returnFocus?: HTMLElement | null;
};

let registeredControls = 0;
const availabilityListeners = new Set<() => void>();

function emitAvailabilityChange() {
  availabilityListeners.forEach((listener) => listener());
}

function subscribeToAvailability(listener: () => void) {
  availabilityListeners.add(listener);
  return () => availabilityListeners.delete(listener);
}

function getAvailabilitySnapshot() {
  return registeredControls > 0;
}

/** Registers the single mounted consent UI as available to contextual triggers. */
export function registerGoogleConsentControls() {
  registeredControls += 1;
  emitAvailabilityChange();

  let registered = true;
  return () => {
    if (!registered) return;
    registered = false;
    registeredControls = Math.max(0, registeredControls - 1);
    emitAvailabilityChange();
  };
}

/** True only while a non-suppressed tenant consent UI is mounted. */
export function useGoogleConsentControlsAvailable() {
  return React.useSyncExternalStore(
    subscribeToAvailability,
    getAvailabilitySnapshot,
    () => false,
  );
}

/** Opens the mounted consent UI and supplies the element that should regain focus. */
export function requestGoogleConsentControls(
  returnFocus?: HTMLElement | null,
) {
  if (typeof window === "undefined") return false;

  const event = new CustomEvent<GoogleConsentOpenDetail>(
    GOOGLE_CONSENT_OPEN_EVENT,
    {
      cancelable: true,
      detail: { returnFocus },
    },
  );

  return !window.dispatchEvent(event);
}

export type { GoogleConsentOpenDetail };
